import { maybeWebContext } from "../search";
// chat/brain.ts — the chat "brain" for Oriora Mood (mirrors Oriora Expense's src/chat/brain.ts).
// SERVER-FIRST: askBrain makes ONE billed call to the generic server chat route
// (POST /api/apps/mood_tracker/chat) with the user's message, the CHAT_ACTIONS catalog, a small
// context string (mood scale + tags in use), and the recent turns. The server model — which knows
// the CURRENT DATE, so "yesterday"/"last week" resolve — returns an ARRAY of actions ("log Monday
// good and Tuesday rough" = two actions; G17) with loose params. toAction() then coerces every
// param defensively (dates YYYY-MM-DD, scores 1..5, tags normalized) and resolves date/term
// references to LOCAL row ids — with fuzzy name matching (Pattern 6) — before anything executes.
//
// FALLBACK: if the server can't be reached (signed out / offline / out of funds / 5xx), askBrain
// falls back to the ORIGINAL on-device regex/keyword router below (routeMessage) — kept fully
// intact, free, no network:
//   • "log good today with work"          → parse locally      → add_entry
//   • "delete yesterday"                   → resolve id locally → delete_entry (UI confirms)
//   • "set today's score to 5" / "tag …"   → resolve id locally → edit_entry
//   • "reflect on my week / insights"      → reuses LIVE reflect-week endpoint (in executeAction) → reflect
//   • "summarize / how am I doing"         → local weekly trend → summarize
//   • "show good days this week" etc.      → local filter parse → filter
//   • anything else                        → none + a short clarifying nudge

import { BrainError, brainCall } from "../brain";
import {
  store, normalizeTags, normalizeTag, todayLocal, formatLocalDate,
  emojiForScore, labelForScore, MOOD_EMOJI, type MoodEntry, type MoodEntryPatch,
} from "../store";
import type { ChatAction, FilterRow, MoodFilter } from "./actions";
import { libraryFind, librarySummary } from "../library";
import { findNamedMatches, suggestNames, notFoundMessage } from "./fuzzy";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; executeAction supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — "log Mon good and Tue rough" = two actions.
}

const HELP =
  'I can log moods, edit or delete them, search, summarize, reflect, answer questions about your own patterns, rename or remove a tag everywhere, export a CSV, and back everything up to your own GitHub or Drive. Try:\n' +
  '• "log good today with work"\n' +
  '• "show my rough days this month"\n' +
  '• "which day am I usually lowest?"\n' +
  '• "set today\'s score to 5" · "rename tag work to job"\n' +
  '• "summarize this week" · "reflect on my week"\n' +
  '• "back up my moods" · "delete yesterday"';

// The app's declared scope for the server chat brain (BRAIN-PATTERNS Pattern 5; the server
// truncates at 400 chars, so the HARD LIMITS come FIRST). Tracking / recaps / observational
// pattern description = Tier A/B; mental-health ADVICE = the Tier-C line, plainly declined.
const SCOPE =
  "Hard limits: never give mental-health, medical or crisis advice — no diagnosis, no therapy, coping or treatment suggestions; decline that plainly and point to a qualified professional. " +
  "This app is a personal daily mood log: log one mood (1-5) a day with tags and a note; edit, search, summarize, and describe patterns in the user's OWN logged data, observationally; back up to the user's own cloud.";

// ── PRE-LLM DISTRESS FENCE (client, deterministic; shared pattern set with Forever Paw / Journal) ──
// A mood log is exactly where acute self-harm / crisis phrasing surfaces. Such a message must NOT
// route to an "observational reflection" AI reply (nor bill a call) — surface human support instead.
// Deliberately narrow (acute self-harm phrasing only) so ordinary low/rough moods pass straight through.
const DISTRESS_PATTERNS: RegExp[] = [
  /\bkill myself\b/i, /\bkilling myself\b/i, /\bend my life\b/i, /\bending my life\b/i,
  /\btake my (own )?life\b/i, /\bwant to die\b/i, /\bdon'?t want to (be alive|live)\b/i,
  /\bno reason to live\b/i, /\bcan'?t go on (without|any longer)\b/i, /\bsuicidal\b/i,
  /\bcommit(?:ting)? suicide\b/i, /\bharm(?:ing)? myself\b/i, /\bhurt(?:ing)? myself\b/i,
  /\bend(?:ing)?\s+it\s+all\b/i, /\b(?:isn'?t|ain'?t|not|no longer)\s+worth\s+living\b/i,
  /\bnothing\s+to\s+live\s+for\b/i,
];
function hasDistressText(text: string): boolean {
  return DISTRESS_PATTERNS.some((re) => re.test(text));
}
const CRISIS_MESSAGE =
  "It sounds like you may be going through something very hard. Oriora Mood is a personal mood log — " +
  "it isn't crisis support. Please reach out to someone who can help right now: in the US call or text 988 " +
  "(Suicide & Crisis Lifeline); elsewhere, find a local line at findahelpline.com.";

// Word → 1..5 mapping (Rough 1 · Meh 2 · Okay 3 · Good 4 · Great 5). Hyphen-free keys only.
const WORD_SCORE: Record<string, number> = {
  great: 5, amazing: 5, awesome: 5, fantastic: 5, excellent: 5, wonderful: 5,
  good: 4, happy: 4, fine: 4, nice: 4,
  okay: 3, ok: 3, neutral: 3, alright: 3, average: 3,
  meh: 2, blah: 2,
  rough: 1, bad: 1, awful: 1, terrible: 1, horrible: 1, low: 1, sad: 1, down: 1,
};

const fmtDay = (iso: string) =>
  new Date(iso.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });

const entryLabel = (e: MoodEntry) => `${fmtDay(e.logged_on)} (${emojiForScore(e.score)} ${labelForScore(e.score)})`;

// Query-shaped → local filter parse. Broad, but only reached AFTER add/delete/edit/reflect/summarize.
const QUERY_RE = /\b(show|find|list|search|which|what|how\s+many|count|days?|entries|moods?|tagged|filter|since|between|over|under|above|below|good\s+days|rough\s+days|last\s+\d+)\b/i;
// Summarize keywords (checked before filter so "how am I doing" ≠ a filter).
const SUMMARIZE_RE = /\b(summar(?:y|ise|ize)|overview|recap|how\s+am\s+i|how\s+have\s+i|how\s+was\s+my|how'?s\s+my)\b/i;
// Reflect = the AI weekly reflection (reuses the live reflect-week endpoint).
const REFLECT_RE = /\b(reflect|reflection|insight|insights|pattern|patterns|analy[sz]e|analysis)\b/i;

function parseDate(text: string): string | null {
  const iso = text.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (iso) return iso[1];
  if (/\btoday\b/i.test(text)) return todayLocal();
  if (/\byesterday\b/i.test(text)) { const d = new Date(); d.setDate(d.getDate() - 1); return formatLocalDate(d); }
  return null;
}

function parseScore(text: string): number | null {
  const low = text.toLowerCase();
  let m = low.match(/\b([1-5])\s*(?:\/\s*5|out\s+of\s+5)\b/);
  if (m) return Number(m[1]);
  m = low.match(/\b(?:score|rate|rated|rating|feeling|felt|mood|a)\s+(?:it\s+)?(?:a\s+|as\s+)?([1-5])\b/);
  if (m) return Number(m[1]);
  for (let i = 0; i < MOOD_EMOJI.length; i++) if (text.includes(MOOD_EMOJI[i])) return i + 1;
  for (const [w, s] of Object.entries(WORD_SCORE)) if (new RegExp(`\\b${w}\\b`, "i").test(low)) return s;
  // Bare digit 1-5 as a last resort — ignore digits that are part of an ISO date.
  m = low.replace(/\d{4}-\d{2}-\d{2}/g, "").match(/\b([1-5])\b/);
  if (m) return Number(m[1]);
  return null;
}

const TAG_STOP = new Set(["today", "yesterday", "and", "note", "the", "my", "a"]);
function parseTags(text: string): string[] {
  const tags: string[] = [];
  for (const h of text.match(/#([a-z0-9-]+)/gi) || []) tags.push(h.slice(1));
  const withM = text.match(/\b(?:with|tags?:?|tagged)\s+([a-z0-9,\s-]+?)(?:\s+(?:note|because)\b|$)/i);
  if (withM) for (const part of withM[1].split(/[,\s]+/)) if (part && !TAG_STOP.has(part.toLowerCase())) tags.push(part);
  return normalizeTags(tags);
}

function parseNote(text: string): string | null {
  let m = text.match(/\bnote[:\-]\s*(.+)$/i);
  if (m) return m[1].trim();
  m = text.match(/\bbecause\s+(.+)$/i);
  if (m) return m[1].trim();
  m = text.match(/["“](.+?)["”]/);
  if (m) return m[1].trim();
  return null;
}

function pickList(rows: MoodEntry[]): string {
  return rows.slice(0, 5).map((e) => `• ${fmtDay(e.logged_on)} — ${emojiForScore(e.score)} ${labelForScore(e.score)}`).join("\n");
}

// Resolve a target entry from a natural term: a date word/ISO → that day; else a note/tag
// substring; else FUZZY over the entry's own words (Pattern 6 — misspellings, dropped words).
const entryName = (e: MoodEntry) => `${e.tags.join(" ")} ${e.note}`;
// Domain filler the user wraps around the identifying words ("the ofsite ENTRY") — dropped
// before fuzzy, because the every-token rule would otherwise demand a match for "entry" too.
const fuzzyTerm = (q: string) =>
  q.replace(/\b(entry|entries|mood|moods|day|days|log|logs|one)\b/gi, " ").replace(/\s+/g, " ").trim();
async function findEntryForTerm(term: string): Promise<MoodEntry[]> {
  const date = parseDate(term);
  if (date) { const e = await store.getEntryForDate(date); return e ? [e] : []; }
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listEntries({ limit: 50000 });
  const exact = all.filter((e) => e.note.toLowerCase().includes(q) || e.tags.some((tg) => tg.includes(q)));
  if (exact.length) return exact;
  const fq = fuzzyTerm(q);
  if (!fq) return [];
  const wrapped = all.map((e) => ({ name: entryName(e), e }));
  return findNamedMatches(wrapped, fq).map((w) => w.e);
}

// Resolve a tag term against the tags actually in use — exact first, then fuzzy, then a
// "did you mean" offer (Pattern 6: never a dead "couldn't find").
async function resolveTag(term: string): Promise<{ tag: string } | ChatAction> {
  const t = normalizeTag(term);
  if (!t) return none("Which tag?");
  const all = await store.listEntries({ limit: 50000 });
  const inUse = [...new Set(all.flatMap((e) => e.tags))];
  if (inUse.includes(t)) return { tag: t };
  const named = inUse.map((name) => ({ name }));
  const hits = findNamedMatches(named, t);
  if (hits.length === 1) return { tag: hits[0].name };
  if (hits.length > 1) return none(`Which tag — ${hits.slice(0, 3).map((h) => h.name).join(", ")}?`);
  return none(notFoundMessage(named, t));
}

// ── Server-first chat brain ─────────────────────────────────────────────────────
// The action catalog sent with every chat call — the SAME actions the local ChatAction union
// supports, described for the model. Params come back loose; toAction() coerces + resolves.
interface ChatActionSpec {
  name: string;
  description: string;
  params?: Record<string, string>;
}


// Standing instructions (fleet standard): the customer shapes the app through chat; stored
// on-device, injected into every brain call once this app runs the server-first chat.
const INSTRUCTIONS_KEY = "oriora.mood-tracker.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS: ChatActionSpec[] = [
  {
    name: "add_entry",
    description: "Log the user's mood. A day can hold SEVERAL entries — logging again adds another one, it does not overwrite the earlier one. Use this whenever they say how they feel or felt, including for an earlier day or an earlier time.",
    params: {
      score: "REQUIRED. Integer 1-5 on the mood scale: 1=Rough, 2=Meh, 3=Okay, 4=Good, 5=Great.",
      date: "Day being logged, YYYY-MM-DD (resolve words like today/yesterday). Defaults to today.",
      time: "Optional time of day as HH:MM 24h, when they say one (\"this morning\" ~08:00, \"at lunch\" ~13:00, \"tonight\" ~21:00). Omit if they did not indicate a time.",
      note: "Optional short free-text note the user gave.",
      tags: 'Optional comma-separated activity tags, lowercase single words (e.g. "work, sleep").',
    },
  },
  {
    name: "edit_entry",
    description: "Change an existing day's score or note, or add tags to it. Give at least one of score/note/tags.",
    params: {
      target: "REQUIRED. Which entry: a YYYY-MM-DD date (resolve today/yesterday), or a distinctive word from its note or tags.",
      score: "New integer score 1-5, only if the user is changing the score.",
      note: "New note text (replaces the old note), only if the user is changing the note.",
      tags: "Comma-separated tags to ADD to the entry, only if the user is tagging it.",
    },
  },
  {
    name: "delete_entry",
    description: "Delete one mood entry. The app asks the user to confirm before it deletes.",
    params: {
      target: "REQUIRED. Which entry: a YYYY-MM-DD date (resolve today/yesterday), or a distinctive word from its note or tags.",
    },
  },
  {
    name: "filter",
    description: 'Search the mood log and show matching days. Use for "show / find / list / how many / which days" questions. All params optional.',
    params: {
      from: "Start date YYYY-MM-DD inclusive (resolve phrases like this week / last 30 days).",
      to: "End date YYYY-MM-DD inclusive.",
      min_score: "Minimum score, integer 1-5 (good days = 4+, great days = 5).",
      max_score: "Maximum score, integer 1-5 (rough days = 2 and under).",
      tag: "One activity tag to match, lowercase.",
      text: "Free text to match inside the day's note.",
    },
  },
  {
    name: "summarize",
    description: 'Show the weekly mood trend + logging streak, computed on-device. Use for "summarize / how am I doing". No params.',
  },
  {
    name: "reflect",
    description: 'Run the AI weekly reflection over recent mood patterns. Use for "reflect / insights / patterns / analyze". No params.',
  },
  {
    name: "ask",
    description:
      'Answer a QUESTION about the user\'s own mood patterns using the AI over on-device computed signals (weekday averages, tag links, window shifts). Use for pattern questions the plain filter can\'t answer — "which day am I usually lowest?", "does work go with worse moods?". Observational only — never advice.',
    params: { question: "the user's question, as they asked it" },
  },
  {
    name: "rename_tag",
    description: "Rename an activity tag EVERYWHERE it appears, across all entries.",
    params: { from: "the tag's current name as the user wrote it", to: "the new tag name" },
  },
  {
    name: "delete_tag",
    description: "Remove an activity tag from EVERY entry that has it (the app asks the user to confirm first).",
    params: { tag: "which tag to remove, as the user wrote it" },
  },
  {
    name: "export_csv",
    description: "Export all mood entries as a CSV file saved where the user picks on this device. Use for 'export my data / export CSV / give me a spreadsheet'.",
    params: {},
  },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole mood log — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  {
    name: "library_save",
    description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in `content`, give it a clear title, and put a short summary in your reply.",
    params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" },
  },
  {
    name: "library_list",
    description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what\'s in my library / in folder X').",
    params: { folder: "optional folder to filter by" },
  },
  {
    name: "library_remove",
    description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — mood entries have their own delete action.",
    params: { title: "which item (its title, from LIBRARY in the context)" },
  },
  {
    name: "library_move",
    description: "Move a library item into a folder ('move the summer plan into Plans').",
    params: { title: "which item", folder: "target folder name" },
  },
  {
    name: "library_merge",
    description: "Merge two or more library items into one (the app asks the customer to confirm first).",
    params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" },
  },
];

// Small app-data summary so the model can resolve references (mirrors Expense sending its
// category names). Best-effort — chat still works if the store read fails.
async function buildContext(): Promise<string> {
  const parts = [`LIBRARY (their saved plans/notes): ${librarySummary()}.`,
    "Mood scale: 1=Rough, 2=Meh, 3=Okay, 4=Good, 5=Great. One entry per calendar day."];
  try {
    const all = await store.listEntries({ limit: 1000 });
    const tags = new Set<string>();
    for (const e of all) { for (const tg of e.tags) tags.add(tg); if (tags.size >= 30) break; }
    if (tags.size) parts.push(`Tags in use: ${[...tags].join(", ")}.`);
    if (all.length) parts.push(`${all.length} entries logged.`);
  } catch { /* context is optional */ }
  return parts.join(" ");
}

// ── Defensive coercers — server params arrive loose ────────────────────────────
const asStr = (v: unknown): string =>
  typeof v === "string" ? v.trim() : typeof v === "number" && Number.isFinite(v) ? String(v) : "";

/** 1..5 integer, or a mood word ("good" → 4); null when absent/invalid. */
function asScore(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" ? parseFloat(v) : NaN;
  if (Number.isFinite(n)) { const s = Math.round(n); if (s >= 1 && s <= 5) return s; }
  const w = asStr(v).toLowerCase();
  return w in WORD_SCORE ? WORD_SCORE[w] : null;
}

const asDate = (v: unknown): string | null => {
  const m = asStr(v).match(/\b(\d{4}-\d{2}-\d{2})\b/);
  return m ? m[1] : null;
};

/** "HH:MM" 24h, or undefined. Deliberately strict — a misread time writes the wrong moment into
 *  the customer's record, and "omitted" just means "now", which is always defensible. */
const asTime = (v: unknown): string | undefined => {
  const t = typeof v === "string" ? v.trim() : "";
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return undefined;
  const h = Number(m[1]), min = Number(m[2]);
  if (h > 23 || min > 59) return undefined;
  return `${String(h).padStart(2, "0")}:${m[2]}`;
};

const asTagList = (v: unknown): string[] => {
  if (Array.isArray(v)) return normalizeTags(v.filter((x): x is string => typeof x === "string"));
  const s = asStr(v);
  return s ? normalizeTags(s.split(/[,\s]+/)) : [];
};

const none = (message: string): ChatAction => ({ kind: "none", message });

// Resolve a loose target (date or note/tag word) to EXACTLY ONE local entry, or a `none`
// action explaining why not. On zero matches it OFFERS the closest entries instead of a
// dead end (Pattern 6's "did you mean").
async function resolveOne(term: string): Promise<MoodEntry | ChatAction> {
  const key = term.trim() || "today";
  const rows = await findEntryForTerm(key);
  if (rows.length === 1) return rows[0];
  if (rows.length > 1) return none(`${rows.length} entries match "${key}" — a date works best:\n${pickList(rows)}`);
  const all = await store.listEntries({ limit: 50000 });
  const wrapped = all.map((e) => ({ name: entryName(e), e }));
  const near = suggestNames(wrapped, fuzzyTerm(key.toLowerCase()) || key, 3)
    .map((n) => wrapped.find((w) => w.name === n)?.e)
    .filter((e): e is MoodEntry => !!e);
  if (near.length) return none(`I couldn't find an entry for "${key}" — did you mean:\n${pickList(near)}`);
  return none(`I couldn't find an entry for "${key}".`);
}

// Server action + loose params → a validated, executable ChatAction (or `none` + ONE helpful line).
// Exported so the offline harness can drive the server-shaped path without a billed call.
export async function toAction(name: string, params: Record<string, unknown>, userText: string, reply: string): Promise<ChatAction> {
  // ── ARTIFACT TOOLS (the library) — handled EARLY, above every app-specific
  // precondition. The library belongs to the CUSTOMER, not to a currently-active
  // record, so no app guard may sit above it (Garage shipped exactly that bug and
  // Outgrown gated its whole chat panel the same way, 2026-08-13).
  // NOTE: this app's toAction returns a ChatAction DIRECTLY (no {reply, action}
  // wrapper), which is why the fleet codemod refused to stamp it — hand-written.
  if (name === "library_save") {
    const content = asStr(params.content);
    if (!content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const rawKind = (asStr(params.item_kind) ?? "note").toLowerCase();
    const itemKind = rawKind === "lesson" || rawKind === "plan" ? rawKind : "note";
    return { kind: "library_save", title: asStr(params.title) ?? "Untitled", itemKind, content, folder: asStr(params.folder) };
  }
  if (name === "library_list") return { kind: "library_list", folder: asStr(params.folder) };
  if (name === "library_remove") {
    const r = resolveLibraryItem(asStr(params.title));
    if (!r.item) return none(r.problem!);
    return { kind: "library_remove", id: r.item.id, label: `"${r.item.title}"` };
  }
  if (name === "library_move") {
    const folder = asStr(params.folder);
    if (!folder) return none("Which folder should it go to?");
    const r = resolveLibraryItem(asStr(params.title));
    if (!r.item) return none(r.problem!);
    return { kind: "library_move", id: r.item.id, folder, label: `"${r.item.title}"` };
  }
  if (name === "library_merge") {
    const titles = (asStr(params.titles) ?? "").split("|").map((t) => t.trim()).filter(Boolean);
    if (titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const ids: string[] = [];
    for (const t of titles) {
      const r = resolveLibraryItem(t);
      if (!r.item) return none(r.problem!);
      ids.push(r.item.id);
    }
    return { kind: "library_merge", ids, title: asStr(params.new_title) ?? titles.join(" + "), label: titles.map((t) => `"${t}"`).join(" + ") };
  }

  switch (name) {
    case "add_entry": {
      const score = asScore(params.score);
      if (score === null) return none('What score, 1–5? (1=Rough … 5=Great) — try "log good today".');
      return {
        kind: "add_entry",
        score,
        logged_on: asDate(params.date) ?? todayLocal(),
        logged_at: asTime(params.time),
        note: asStr(params.note) || null,
        tags: asTagList(params.tags),
      };
    }

    case "edit_entry": {
      const e = await resolveOne(asStr(params.target));
      if ("kind" in e) return e;
      const patch: MoodEntryPatch = {};
      const bits: string[] = [];
      const score = asScore(params.score);
      if (score !== null) { patch.score = score; bits.push(`score → ${emojiForScore(score)} ${labelForScore(score)}`); }
      const note = asStr(params.note);
      if (note) { patch.note = note; bits.push("note"); }
      const addTags = asTagList(params.tags);
      if (addTags.length) { patch.tags = normalizeTags([...e.tags, ...addTags]); bits.push(`+${addTags.join(", ")}`); }
      if (bits.length === 0) return none("What should I change — the score (1–5), the note, or tags?");
      return { kind: "edit_entry", id: e.id, patch, label: `${fmtDay(e.logged_on)} (${bits.join(" · ")})` };
    }

    case "delete_entry": {
      const e = await resolveOne(asStr(params.target));
      if ("kind" in e) return e;
      return { kind: "delete_entry", id: e.id, label: entryLabel(e) };
    }

    case "filter": {
      const f: MoodFilter = {};
      const from = asDate(params.from);
      if (from) f.from = from;
      const to = asDate(params.to);
      if (to) f.to = to;
      const min = asScore(params.min_score ?? params.minScore);
      if (min !== null) f.minScore = min;
      const max = asScore(params.max_score ?? params.maxScore);
      if (max !== null) f.maxScore = max;
      const tag = asStr(params.tag);
      if (tag) {
        // Fuzzy-resolve the tag against the tags in use, so "slee" / "wrok" still lands (Pattern 6).
        const t = await resolveTag(tag);
        if ("kind" in t) return t;
        f.tag = t.tag;
      }
      const text = asStr(params.text);
      if (text) f.text = text;
      return { kind: "filter", filter: f, label: userText };
    }

    case "summarize":
      return { kind: "summarize" };

    case "reflect":
      return { kind: "reflect" };

    case "ask": {
      const question = asStr(params.question) || userText;
      if (!question) return none('What would you like to know about your moods? e.g. "which day am I usually lowest?"');
      return { kind: "ask", question };
    }

    case "rename_tag": {
      const to = normalizeTag(asStr(params.to));
      if (!to) return none('Tell me both names — e.g. "rename tag work to job".');
      const t = await resolveTag(asStr(params.from));
      if ("kind" in t) return t;
      return { kind: "rename_tag", from: t.tag, to };
    }

    case "delete_tag": {
      const t = await resolveTag(asStr(params.tag));
      if ("kind" in t) return t;
      return { kind: "delete_tag", tag: t.tag };
    }

    case "export_csv":
      return { kind: "export_csv" };

    case "backup_save":
      return { kind: "backup_save" };
    case "backup_status":
      return { kind: "backup_status" };
    case "backup_restore": {
      const prov = asStr(params.provider);
      return { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined };
    }
    case "save_local_copy":
      return { kind: "save_local_copy" };
    case "import_file":
      return { kind: "import_file" };
    case "save_to_cloud": {
      const contentKind = /conversation|chat|messages|prompts/i.test(asStr(params.content_kind)) ? "conversation" : "note";
      const dest = asStr(params.destination);
      const destination = /drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined;
      return { kind: "save_to_cloud", contentKind, note: asStr(params.note) || asStr(params.content) || undefined, destination };
    }
    case "remember": {
      const instruction = asStr(params.instruction);
      if (!instruction) return none("What should I remember?");
      return { kind: "remember", instruction };
    }
    default: // 'none' or anything unrecognized → the server's reply IS the message.
      return none(reply || HELP);
  }
}

// The server sends BOTH `actions` (the array) and back-compat `action`/`params` = the first
// element. Reading only the singular field is why "log Mon good and Tue rough" silently did
// half the work (G17) — so read the ARRAY, with the singular shape as an older-server fallback.
interface ServerAct { name?: string; action?: string; params?: unknown }
interface ServerChat { actions?: ServerAct[]; action?: unknown; params?: unknown; reply?: unknown }

const okParams = (p: unknown): Record<string, unknown> =>
  p && typeof p === "object" && !Array.isArray(p) ? (p as Record<string, unknown>) : {};

/** ONE billed call to the generic chat route; the app's own brainCall targets /api/apps/mood_tracker/. */
export async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const _notes = getInstructions();
  const context = (await buildContext()) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  // The caller appends the current user message as the last turn — send the 20 PRIOR turns (server cap).
  const prior = history.slice(0, -1).slice(-20).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", {
    message: text + (await maybeWebContext(text)),
    actions: CHAT_ACTIONS,
    context,
    history: prior,
    scope: SCOPE,
  }, onChunk);
  const reply = typeof r.reply === "string" ? r.reply.trim() : "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (typeof r.action === "string" && r.action.trim() && r.action !== "none"
      ? [{ action: r.action, params: r.params }] // back-compat / older server
      : []);
  // "actions": [] = a question, a decline, or a clarification — the reply IS the answer.
  if (!raw.length) return { reply: "", actions: [none(reply || HELP)] };

  const actions: ChatAction[] = [];
  for (const a of raw) {
    const name = typeof a.name === "string" ? a.name : typeof a.action === "string" ? a.action : "";
    // A per-item failure ("which tag?") must not silently drop the item — the `none` carries it.
    actions.push(await toAction(name.trim(), okParams(a.params), text, reply));
  }
  // A lone `none` carries its ONE message in action.message — don't double it with the preface.
  const loneNone = actions.length === 1 && actions[0].kind === "none";
  return { reply: loneNone ? "" : reply, actions };
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) return { reply: "", actions: [none(HELP)] };
  // Pre-LLM safety: acute self-harm/crisis phrasing → human support, never the model (no billed call).
  if (hasDistressText(text)) return { reply: "", actions: [none(CRISIS_MESSAGE)] };
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Signed out / offline / out of funds / server trouble → the free on-device router.
    return routeMessage(text);
  }
}

const one = (action: ChatAction): BrainResponse => ({ reply: "", actions: [action] });

export async function routeMessage(text: string): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return one({ kind: "none", message: HELP });
  const low = t.toLowerCase();

  // 1) ADD — explicit log verb, OR a feeling phrase that carries a parseable score.
  const isAdd = /^(?:log|add|record|track|save)\b/i.test(t)
    || (/\b(i'?m|i\s+am|i\s+feel|i\s+felt|feeling|felt|i\s+was|mood)\b/i.test(low) && parseScore(t) !== null);
  if (isAdd && !/^\b(?:add|save)\s+tags?\b/i.test(t)) {
    const score = parseScore(t);
    if (score === null) return one({ kind: "none", message: 'What score, 1–5? Try "log good today" or "log 4/5 with work".' });
    return one({ kind: "add_entry", score, logged_on: parseDate(t) ?? todayLocal(), note: parseNote(t), tags: parseTags(t) });
  }

  // 2) TAG OPS — before the generic delete so "remove tag work" isn't read as a delete-entry.
  let m = t.match(/^rename\s+(?:the\s+)?tag\s+(.+?)\s+to\s+(.+)$/i);
  if (m) {
    const r = await resolveTag(m[1]);
    if ("kind" in r) return one(r);
    return one({ kind: "rename_tag", from: r.tag, to: normalizeTag(m[2]) });
  }
  m = t.match(/^(?:delete|remove)\s+(?:the\s+)?tag\s+(.+)$/i);
  if (m) {
    const r = await resolveTag(m[1]);
    if ("kind" in r) return one(r);
    return one({ kind: "delete_tag", tag: r.tag });
  }

  // 3) EXPORT — CSV to a location the user picks.
  if (/\bexport\b/i.test(low) && /\b(csv|spreadsheet|data|entries|moods)\b/i.test(low)) {
    return one({ kind: "export_csv" });
  }

  // 4) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove)\b\s*(.*)$/i);
  if (delM) {
    const term = (delM[1].trim().replace(/^(?:the|my|mood|entry|for|from|on)\s+/i, "").trim()) || "today";
    const rows = await findEntryForTerm(term);
    if (rows.length === 0) return one({ kind: "none", message: `I couldn't find an entry for "${term}".` });
    if (rows.length > 1) return one({ kind: "none", message: `I found ${rows.length} entries matching "${term}" — a date works best:\n${pickList(rows)}` });
    const e = rows[0];
    return one({ kind: "delete_entry", id: e.id, label: entryLabel(e) });
  }

  // 5) EDIT — score / note / tags on a resolved entry (single-match only).
  const edit = await routeEdit(t);
  if (edit) return edit;

  // 6) REFLECT — the AI weekly reflection (billed; reuses the live reflect-week endpoint).
  if (REFLECT_RE.test(low)) return one({ kind: "reflect" });

  // 7) SUMMARIZE — local weekly trend + streak (free).
  if (SUMMARIZE_RE.test(low)) return one({ kind: "summarize" });

  // 8) FILTER / SEARCH — query-shaped → local filter parse.
  if (QUERY_RE.test(low)) {
    const filter = buildFilter(t);
    return one({ kind: "filter", filter, label: t });
  }

  // 9) Unclear → a short clarifying nudge.
  return one({ kind: "none", message: HELP });
}

// EDIT intents. Returns null if the message isn't an edit so the router can fall through.
async function routeEdit(t: string): Promise<BrainResponse | null> {
  // "set/change/update [date]('s) score|mood|rating to X"
  let m = t.match(/^(?:set|change|update)\s+(.*?)(?:'s)?\s+(?:score|mood|rating)\s+to\s+(.+)$/i);
  if (m) {
    const score = parseScore(m[2]);
    if (score === null) return one({ kind: "none", message: "What score (1–5) should it be?" });
    return resolveEdit(m[1], { score }, `score → ${emojiForScore(score)} ${labelForScore(score)}`);
  }

  // "add tag(s) X[, Y] to [date]"
  m = t.match(/^(?:add\s+)?tags?\s+(.+?)\s+to\s+(.+)$/i);
  if (m) {
    const newTags = normalizeTags(m[1].split(/[,\s]+/));
    return newTags.length ? resolveEditMergeTags(m[2], newTags) : null;
  }
  // "tag [date] with X[, Y]"
  m = t.match(/^tag\s+(.+?)\s+with\s+(.+)$/i);
  if (m) {
    const newTags = normalizeTags(m[2].split(/[,\s]+/));
    return newTags.length ? resolveEditMergeTags(m[1], newTags) : null;
  }

  // "set note (for) [date] to ..." / "note (for) [date]: ..."
  m = t.match(/^(?:set\s+)?note\s+(?:for\s+)?(.+?)\s+(?:to|:)\s+(.+)$/i);
  if (m) return resolveEdit(m[1], { note: m[2].trim() }, "note");

  return null;
}

async function resolveEdit(term: string, patch: MoodEntryPatch, note?: string): Promise<BrainResponse> {
  const e = await resolveOne(term);
  if ("kind" in e) return one(e);
  const label = note ? `${fmtDay(e.logged_on)} (${note})` : fmtDay(e.logged_on);
  return one({ kind: "edit_entry", id: e.id, patch, label });
}

async function resolveEditMergeTags(term: string, newTags: string[]): Promise<BrainResponse> {
  const e = await resolveOne(term);
  if ("kind" in e) return one(e);
  const merged = normalizeTags([...e.tags, ...newTags]);
  return one({ kind: "edit_entry", id: e.id, patch: { tags: merged }, label: `${fmtDay(e.logged_on)} (+${newTags.join(", ")})` });
}

// Local filter parse — score band, date window, tag, note text. No server call.
function buildFilter(text: string): MoodFilter {
  const low = text.toLowerCase();
  const f: MoodFilter = {};

  // Score band (word-shaped).
  if (/\bgreat\b/.test(low)) f.minScore = 5;
  else if (/\b(good|happy|best)\b/.test(low)) f.minScore = 4;
  else if (/\b(rough|bad|low|worst|awful|sad|terrible)\b/.test(low)) f.maxScore = 2;
  else if (/\b(okay|meh|average|neutral)\b/.test(low)) { f.minScore = 2; f.maxScore = 3; }
  // Numeric overrides.
  const ge = low.match(/(?:>=|at\s+least|score\s+of\s+|above|over)\s*([1-5])/);
  if (ge) f.minScore = Number(ge[1]);
  const le = low.match(/(?:<=|at\s+most|below|under)\s*([1-5])/);
  if (le) f.maxScore = Number(le[1]);
  const eq = low.match(/\b([1-5])\s*(?:\/\s*5|out\s+of\s+5)\b/);
  if (eq) { f.minScore = Number(eq[1]); f.maxScore = Number(eq[1]); }

  // Date window.
  const setFrom = (daysBack: number) => { const d = new Date(); d.setDate(d.getDate() - daysBack); f.from = formatLocalDate(d); };
  if (/\btoday\b/.test(low)) { f.from = todayLocal(); f.to = todayLocal(); }
  else if (/\byesterday\b/.test(low)) { const d = new Date(); d.setDate(d.getDate() - 1); const s = formatLocalDate(d); f.from = s; f.to = s; }
  else if (/\b(this\s+week|past\s+week|last\s+7\s+days)\b/.test(low)) setFrom(6);
  else if (/\b(this\s+fortnight|last\s+14\s+days|two\s+weeks)\b/.test(low)) setFrom(13);
  else if (/\b(this\s+month|past\s+month|last\s+30\s+days)\b/.test(low)) setFrom(29);
  const lastN = low.match(/\blast\s+(\d{1,3})\s+days?\b/);
  if (lastN) setFrom(Number(lastN[1]) - 1);
  const on = low.match(/\bon\s+(\d{4}-\d{2}-\d{2})\b/);
  if (on) { f.from = on[1]; f.to = on[1]; }
  const since = low.match(/\bsince\s+(\d{4}-\d{2}-\d{2})\b/);
  if (since) f.from = since[1];

  // Tag.
  const hash = low.match(/#([a-z0-9-]+)/);
  const tagged = low.match(/\b(?:tagged|tag|with)\s+([a-z][a-z0-9-]*)/);
  if (hash) f.tag = normalizeTag(hash[1]);
  else if (tagged) f.tag = normalizeTag(tagged[1]);

  // Note text.
  const about = low.match(/\b(?:about|mention(?:ing)?|containing|note\s+with)\s+["']?([a-z0-9 ]{2,40})["']?/);
  if (about) f.text = about[1].trim();

  return f;
}


/** Resolve a loose library title to exactly ONE item, or say why not. Mirrors the fleet helper. */
function resolveLibraryItem(term: string | undefined): { item?: { id: string; title: string }; problem?: string } {
  const t = (term ?? "").trim();
  if (!t) return { problem: "Which item? Tell me its title." };
  const hits = libraryFind(t);
  if (hits.length === 0) return { problem: `I couldn't find "${t}" in your library.` };
  if (hits.length > 1) {
    return { problem: `I found ${hits.length} items matching "${t}" — which one?\n${hits.slice(0, 5).map((h) => `• ${h.title}`).join("\n")}` };
  }
  return { item: hits[0] };
}
